import { fetchDelay, resolveBaseUrl } from './helpers'
import { createHttpService } from './gateway'
import type { IFilterModelSwagger, IReponseAll } from './types'
import ServiceBase from './service-base'

/**
 * Base class for HTTP CRUD services.
 *
 * @template T - Entity type.
 * @template K - Entity ID type.
 *
 * Provides common methods:
 * - all: Get list with filter and pagination
 * - single: Get detail by ID
 * - create: Add new entity
 * - update: Update entity by ID
 * - remove: Delete entity by ID
 *
 * Supports request cancellation and artificial delay.
 */
class CRUDServiceBase<T, K> extends ServiceBase {
  constructor(baseUri: string) {
    super(createHttpService(resolveBaseUrl(baseUri)))
  }
  /**
   * Fetches a list of entities with optional filters and pagination.
   *
   * @param filters - Filters and pagination options.
   * @param signal - Optional AbortSignal for request cancellation.
   * @param options - Optional delay configuration.
   * @returns A promise resolving to the response containing the list of entities.
   */
  all = (filters: Partial<IFilterModelSwagger<T>>, signal?: AbortSignal, options?: { delay?: number }) => {
    return fetchDelay(async () => {
      const data = await this.tryPushNotify(this.post<IReponseAll<T>>, 'All', filters, {
        headers: { 'Content-Type': 'application/json' },
        signal
      })
      return data
    }, options?.delay ?? 500)
  }
  /**
   * Fetches a single entity by its ID.
   *
   * @param id - The ID of the entity to fetch.
   * @param signal - Optional AbortSignal for request cancellation.
   * @returns A promise resolving to the entity data.
   */
  single = (id: K, signal?: AbortSignal) => {
    return fetchDelay(async () => {
      const data = await this.tryGet<T>('Get', { params: { Id: id }, signal })
      return data
    }, 500)
  }
  /**
   * Creates a new entity.
   *
   * @param model - The entity data to create.
   * @param signal - Optional AbortSignal for request cancellation.
   * @param options - Optional delay configuration.
   * @returns A promise resolving to the created entity data.
   */
  create = (model: Partial<T>, signal?: AbortSignal, options?: { delay?: number }) => {
    return fetchDelay(async () => {
      const data = await this.pushNotify(this.post<T>, 'Create', model, { signal })
      return data
    }, options?.delay ?? 500)
  }
  /**
   * Updates an existing entity by its ID.
   *
   * @param id - The ID of the entity to update.
   * @param model - The partial entity data to update.
   * @param signal - Optional AbortSignal for request cancellation.
   * @returns A promise resolving to the updated entity data.
   */
  update = (id: K, model: Partial<T>, signal?: AbortSignal) => {
    return fetchDelay(async () => {
      const data = await this.pushNotify(this.put<T>, 'Update', model, { signal, params: { Id: id } })
      return data
    }, 500)
  }
  /**
   * HTTP PATCH request with automatic authentication
   *
   * @param id - The ID of the entity to update.
   * @param model - The partial entity data to update.
   * @param signal - Optional AbortSignal for request cancellation.
   * @returns A promise resolving to the updated entity data.
   */
  updatePatch = (id: K, model: Partial<T>, signal?: AbortSignal) => {
    return fetchDelay(async () => {
      const data = await this.pushNotify(this.patch<T>, 'Patch', model, { signal, params: { Id: id } })
      return data
    }, 500)
  }
  /**
   * Deletes an entity by its ID.
   *
   * @param id - The ID of the entity to delete.
   * @param signal - Optional AbortSignal for request cancellation.
   * @returns A promise resolving to the deletion response.
   */
  remove = (id: K, signal?: AbortSignal) => {
    return fetchDelay(async () => {
      const data = await this.pushNotify(this.delete<T>, 'Delete', { params: { Id: id }, signal })
      return data
    }, 500)
  }
}

export default CRUDServiceBase
